import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid2 as Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Funnel,
  FunnelChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  convertStatusStringToSwedish,
  getStatusColorByString,
  OpportunityStatusString,
} from "../../../lib/utils/statusConverter";
import {
  useGetOpportunityAnalyticsQuery,
  PipelineStageData,
} from "../../../store/services/opportunityService";
import DateRangePicker from "../../date/DateRangePicker";
import { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

// Map numeric opportunity step to the expected string format for the status converter
const mapStepToStatusString = (step: number): OpportunityStatusString => {
  const stepStr = step.toString() as OpportunityStatusString;
  // Make sure the step is within valid range (0-5)
  return ["0", "1", "2", "3", "4", "5"].includes(stepStr) ? stepStr : "0";
};

interface ProcessedPipelineData extends PipelineStageData {
  status: string;
  color: string;
}

interface FunnelDataItem {
  value: number;
  name: string;
  fill: string;
  conversionRate: number;
}

const SalesPipelineDashboard = () => {
  const [appliedStartDate, setAppliedStartDate] = useState<string | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<string | null>(null);
  const { data, isLoading, error } = useGetOpportunityAnalyticsQuery(
    appliedStartDate && appliedEndDate
      ? {
          startDate: appliedStartDate,
          endDate: appliedEndDate,
        }
      : skipToken
  );

  const handleApplyDateRange = (startDate: string, endDate: string) => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const handleClearDateRange = () => {
    setAppliedStartDate(null);
    setAppliedEndDate(null);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Det gick inte att ladda säljtratt-data. Försök igen senare.
      </Alert>
    );
  }

  // Process pipeline data from the updated API response
  const pipelineData: ProcessedPipelineData[] =
    (data?.PipelineAnalytics ?? []).map((stage: PipelineStageData) => {
      // Convert the status to a string that our UI utilities can use
      const statusString =
        typeof stage.Status === "number"
          ? mapStepToStatusString(stage.Status)
          : (stage.Status.toString() as OpportunityStatusString);

      return {
        ...stage,
        status: convertStatusStringToSwedish(statusString),
        color: getStatusColorByString(statusString),
      };
    }) ?? [];

  const funnelData: FunnelDataItem[] = pipelineData.map((item) => ({
    value: item.Count,
    name: item.status,
    fill: item.color,
    conversionRate: item.ConversionRate,
  }));

  return (
    <Stack gap={2}>
      <DateRangePicker
        onClear={handleClearDateRange}
        onApply={handleApplyDateRange}
      />
      <Grid container spacing={3}>
        {/* Pipeline Funnel */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: "400px" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
              Säljtratt Steg
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <FunnelChart>
                  <Tooltip
                    formatter={(value, name, props) => [
                      `Antal: ${value} (${props.payload.conversionRate}% konvertering)`,
                      props.payload.name,
                    ]}
                  />
                  <Funnel dataKey="value" data={funnelData} isAnimationActive>
                    {funnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Value Distribution */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: "400px" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Värdefördelning
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={pipelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" interval={0} height={60} />
                  <YAxis
                    width={80}
                    tickFormatter={(value) =>
                      new Intl.NumberFormat("sv-SE", {
                        notation: "compact",
                        compactDisplay: "short",
                        maximumFractionDigits: 1,
                      }).format(value)
                    }
                  />
                  <Tooltip
                    formatter={(value) =>
                      new Intl.NumberFormat("sv-SE", {
                        style: "currency",
                        currency: "SEK",
                      }).format(+value)
                    }
                  />
                  <Bar dataKey="Value" name="Värde">
                    {pipelineData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Summary Cards */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {pipelineData.map((item) => (
              <Card key={item.status} sx={{ flexGrow: 1, minWidth: "200px" }}>
                <CardContent>
                  <Typography variant="h6" color="text.secondary">
                    {item.status}
                  </Typography>
                  <Typography variant="h4">{item.Count}</Typography>
                  {item.Value && (
                    <Typography variant="body2" color="text.secondary">
                      Värde:{" "}
                      {new Intl.NumberFormat("sv-SE", {
                        style: "currency",
                        currency: "SEK",
                        maximumFractionDigits: 0,
                      }).format(item.Value)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default SalesPipelineDashboard;
